import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";

import { ErrorTypography } from "components/ErrorTypography";

interface Props {
  hasMarkedNoCapacity: boolean;
  isLoading: boolean;
  error: UIError;
  onMark: VoidFunction;
  onUndo: VoidFunction;
  markLabel?: string;
}

const NO_CAPACITY_TOOLTIP =
  "Let the shipper know if you don't have a truck available. You can still quote later if a truck does come available.";

export function NoCapacityButton(props: Props) {
  const {
    hasMarkedNoCapacity,
    isLoading,
    error,
    onMark,
    onUndo,
    markLabel = "No truck available",
  } = props;

  const action = (() => {
    if (isLoading) {
      // Matches the outlined button's own sx below (not a hardcoded height)
      // so the loading state doesn't shrink the row.
      return (
        <Button variant="outlined" sx={{ p: "1rem" }} disabled>
          <CircularProgress size={20} />
        </Button>
      );
    }

    if (hasMarkedNoCapacity) {
      return (
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body1" color="text.secondary">
            You said no truck available
          </Typography>
          <Button variant="text" onClick={onUndo}>
            Undo
          </Button>
        </Stack>
      );
    }

    return (
      <Tooltip title={NO_CAPACITY_TOOLTIP}>
        <Button variant="outlined" sx={{ p: "1rem" }} onClick={onMark}>
          {markLabel}
        </Button>
      </Tooltip>
    );
  })();

  return (
    // This sits in a row beside Submit quote, centered against it (matching
    // the mockup) so the marked state's shorter text/Undo line centers
    // vertically against that taller button. The error is positioned out of
    // flow, absolutely, so its variable height/width never changes this
    // block's own size -- that previously grew the row's tallest item and
    // either shifted this button down (when centered) or right (when the
    // button was end-aligned within a widened column).
    <Box sx={{ position: "relative" }}>
      {action}
      <Box sx={{ position: "absolute", top: "100%", left: 0, mt: 0.5 }}>
        <ErrorTypography error={error} />
      </Box>
    </Box>
  );
}
